<template>
  <div class="newsletter-banner-dialog">
    <v-form ref="form" v-model="isFormValid">
      <div class="section">
        <h4 class="mb-4">State 1 — Subscribe Form</h4>

        <va-tinymce-input
          class="mb-4 w-full"
          title="Content"
          required
          :display-errors="displayErrors"
          :current-content="formData.state1.content"
          :forced-root-block-attrs="{ class: 'newsletter-banner-content' }"
          stripped-down
          @update="content => (formData.state1.content = content)" />

        <v-text-field
          v-model="formData.state1.emailPlaceholder"
          label="Email field placeholder" />

        <v-text-field
          v-model="formData.state1.buttonLabel"
          label="Subscribe button label"
          :rules="requiredRules"
          required />
        <v-select
          v-model="formData.state1.buttonSize"
          :items="fontSizeOptions"
          label="Subscribe button size"
          :menu-props="{ offsetY: true }"
          class="mb-4" />

        <VaColorPicker
          v-model="formData.state1.buttonBgColor"
          title="Button background color"
          position="bottom"
          class="mb-4" />

        <VaColorPicker
          v-model="formData.state1.buttonLabelColor"
          title="Button label color"
          position="bottom"
          class="mb-4" />

        <va-tinymce-input
          class="mb-4 w-full"
          title="Disclaimer"
          :current-content="formData.state1.disclaimerText"
          :forced-root-block-attrs="{ class: 'newsletter-banner-content' }"
          stripped-down
          @update="content => (formData.state1.disclaimerText = content)" />
      </div>

      <div class="section">
        <h4 class="mb-4">State 2 — Thank You</h4>

        <va-tinymce-input
          class="mb-4 w-full"
          title="Content"
          required
          :display-errors="displayErrors"
          :current-content="formData.state2.content"
          :forced-root-block-attrs="{ class: 'newsletter-banner-content' }"
          stripped-down
          @update="content => (formData.state2.content = content)" />

        <va-tinymce-input
          class="mb-4 w-full"
          title="Confirmation message"
          required
          :display-errors="displayErrors"
          :current-content="formData.state2.confirmationMessage"
          :forced-root-block-attrs="{ class: 'newsletter-banner-content' }"
          stripped-down
          @update="content => (formData.state2.confirmationMessage = content)" />
      </div>

      <v-alert v-if="errorMessage" type="error" class="mt-4" dismissible @input="errorMessage = ''">
        {{ errorMessage }}
      </v-alert>
    </v-form>
  </div>
</template>

<script>
  import cloneDeep from "lodash/cloneDeep";
  import { NEWSLETTER_BANNER_EVENT_TYPES } from "@/constant";
  import VaTinymceInput from "@/components/custom/VaTinymceInput";
  import VaColorPicker from "@/components/custom/VaColorPicker";

  const FONT_SIZE_OPTIONS = [8, 10, 12, 14, 16, 18, 19, 20, 22, 24, 26, 28, 30, 32, 34, 36, 40].map(n => ({
    value: n,
    text: `${n}px`,
  }));

  const DEFAULT_FORM_DATA = {
    state1: {
      content:
        '<h3 style="font-size: 32px;">Smart ideas, straight to your inbox.</h3><p class="newsletter-banner-content" style="font-size: 18px;">A short, useful read every Thursday. No fluff — just the updates, tools, and tips worth your time.</p>',
      emailPlaceholder: "your@email.com",
      buttonLabel: "Subscribe",
      buttonSize: 14,
      buttonBgColor: "#355aff",
      buttonLabelColor: "#ffffff",
      disclaimerText:
        '<p class="newsletter-banner-content" style="font-size: 14px;">By subscribing you agree to our Terms and Privacy Policy.</p><p class="newsletter-banner-content" style="font-size: 14px;"><strong>Unsubscribe anytime.</strong></p>',
    },
    state2: {
      content:
        '<h3 style="font-size: 32px;">You\'re subscribed!</h3><p class="newsletter-banner-content" style="font-size: 18px;">We\'re glad to have you. Look out for your first short, useful read this Thursday — no fluff, just the updates, tools, and tips worth your time.</p>',
      confirmationMessage: '<p class="newsletter-banner-content">You\'re on the list.</p>',
    },
  };

  const isNonEmptyHtml = html => {
    if (!html) return false;
    return html
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, "")
      .trim().length > 0;
  };

  export default {
    name: "NewsletterBannerDialog",
    components: { VaTinymceInput, VaColorPicker },
    data() {
      return {
        isFormValid: false,
        displayErrors: false,
        errorMessage: "",
        fontSizeOptions: FONT_SIZE_OPTIONS,
        formData: cloneDeep(DEFAULT_FORM_DATA),
        requiredRules: [v => !!v || this.$t("generic.validation.required")],
      };
    },
    computed: {
      isAllValid() {
        return (
          this.isFormValid &&
          isNonEmptyHtml(this.formData.state1.content) &&
          isNonEmptyHtml(this.formData.state2.content) &&
          isNonEmptyHtml(this.formData.state2.confirmationMessage)
        );
      },
    },
    watch: {
      isAllValid: {
        handler() {
          this.sendError();
        },
      },
      formData: {
        handler() {
          this.validateAndUpdate();
        },
        deep: true,
      },
    },
    beforeMount() {
      this.setupReceiveDataListener();
      this.requestData();
    },
    methods: {
      async validateAndUpdate() {
        if (this.$refs.form) {
          await this.$refs.form.validate();
          if (this.isAllValid) {
            this.updateValue();
            this.errorMessage = "";
          }
        }
      },
      sendError() {
        window.parent.postMessage(
          {
            mceAction: NEWSLETTER_BANNER_EVENT_TYPES.VALIDATION,
            data: { isValid: this.isAllValid },
          },
          origin,
        );
      },
      updateValue() {
        window.parent.postMessage(
          {
            mceAction: NEWSLETTER_BANNER_EVENT_TYPES.UPDATE_NEWSLETTER_BANNER_DATA,
            data: cloneDeep(this.formData),
          },
          origin,
        );
      },
      setupReceiveDataListener() {
        window.onmessage = async message => {
          if (message.data.type === NEWSLETTER_BANNER_EVENT_TYPES.NEED_VALIDATION) {
            this.displayErrors = true;
            await this.$refs.form.validate();
          }
          if (message.data.type === NEWSLETTER_BANNER_EVENT_TYPES.SEND_DATA) {
            const { data } = message.data;
            if (data && Object.keys(data).length > 0) {
              const incoming = cloneDeep(data);
              this.formData = {
                state1: { ...DEFAULT_FORM_DATA.state1, ...(incoming.state1 || {}) },
                state2: { ...DEFAULT_FORM_DATA.state2, ...(incoming.state2 || {}) },
              };
            } else {
              this.updateValue();
            }
          }
        };
      },
      requestData() {
        window.parent.postMessage({ mceAction: NEWSLETTER_BANNER_EVENT_TYPES.REQUEST_DATA }, origin);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .newsletter-banner-dialog {
    margin-bottom: 30px;
    padding: 30px 12px 0;
  }

  .section {
    margin-top: 24px;
    padding: 16px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;

    h4 {
      margin: 0;
      font-weight: 500;
    }
  }
</style>
